import { Injectable, signal } from '@angular/core';

const THEME_INVERTED_KEY = 'mir.themeInverted';

@Injectable({ providedIn: 'root' })
export class NavigationStateService {
  readonly mobileOpen = signal(false);
  readonly openGroup = signal<string | null>(null);
  // Sous-section de 3e niveau ouverte (ex. "Interventions" sous Mon SDIS,
  // "Utilisateurs" sous Administration) -- fermée par défaut, et indépendante
  // du groupe de 2e niveau qui la contient : rouvrir ce dernier ne la
  // rouvre pas automatiquement, elle reste repliée tant qu'on ne la sollicite
  // pas.
  readonly openSubgroup = signal<string | null>(null);
  // Sidebar réduite aux pictogrammes, desktop uniquement (le tiroir mobile
  // a déjà son propre état ci-dessus). Jamais persisté : redémarre toujours
  // dépliée au chargement de la page.
  readonly collapsed = signal(false);
  // Thème sidebar inversé (fond rouge, surbrillance grise) -- persisté,
  // contrairement à collapsed ci-dessus : c'est une préférence durable, pas
  // un état d'affichage transitoire.
  readonly inverted = signal(localStorage.getItem(THEME_INVERTED_KEY) === 'true');

  toggleInverted(): void {
    this.inverted.update(value => {
      const next = !value;
      localStorage.setItem(THEME_INVERTED_KEY, String(next));
      return next;
    });
  }

  toggleMobile(): void {
    this.mobileOpen.update(open => !open);
  }

  closeMobile(): void {
    this.mobileOpen.set(false);
  }

  toggleCollapsed(): void {
    this.collapsed.update(collapsed => !collapsed);
  }

  toggleGroup(id: string): void {
    this.openGroup.update(current => (current === id ? null : id));
  }

  isGroupOpen(id: string): boolean {
    return this.openGroup() === id;
  }

  toggleSubgroup(id: string): void {
    this.openSubgroup.update(current => (current === id ? null : id));
  }

  isSubgroupOpen(id: string): boolean {
    return this.openSubgroup() === id;
  }

  // Clic sur une icône de groupe en mode replié : redéplie la sidebar et
  // ouvre directement ce groupe, plutôt que de togguer un état ouvert/fermé
  // invisible tant que la sidebar reste réduite.
  onGroupClick(id: string): void {
    if (this.collapsed()) {
      this.collapsed.set(false);
      this.openGroup.set(id);
    } else {
      this.toggleGroup(id);
    }
  }
}
